import React, { useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import { makeStyles } from '@material-ui/core/styles'
import { isEqual, sortBy } from 'lodash'
import yup from 'civic-champs-shared/utils/yup'
import { Person } from 'civic-champs-shared/common/types'
import { FormModal, FormModalScreen } from 'components/FormModal'
import RadioGroupFormField from 'components/RadioGroupFormField'
import VolunteerSelector from 'tracking/activity/components/VolunteerSelector'
import { useFeatureEnabled } from 'core/feature/hooks'

const settingsSchema = yup.object({
  automaticHourLogging: yup.boolean().required(),
  applicationNotificationPersons: yup.array().notRequired(),
})

export interface GroupSettingsFormValues {
  automaticHourLogging: boolean
  applicationNotificationPersons: Person[]
}

export interface EditGroupSettingsDialogProps {
  showing: boolean
  close: () => void
  complete: (value: boolean) => void
  group: {
    automaticHourLogging: boolean
    applicationNotificationPersons?: Person[]
  }
  onSubmit: (payload: {
    automaticHourLogging?: boolean
    applicationNotificationPersonIds?: number[]
  }) => Promise<unknown>
}

const useStyles = makeStyles({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    boxSizing: 'border-box',
  },
  choiceSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    width: '100%',
  },
  emailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },
  sectionTitle: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    margin: 0,
  },
  helperText: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#747778',
    margin: 0,
  },
  radioIndent: {
    boxSizing: 'border-box',
    width: '100%',
    paddingLeft: '20px',
    '& .MuiFormGroup-root': {
      gap: '20px',
    },
    '& .MuiFormControlLabel-root': {
      margin: 0,
      alignItems: 'center',
      minHeight: '20px',
    },
    '& .MuiRadio-root': {
      padding: 0,
    },
    '& .MuiIconButton-root': {
      padding: 0,
    },
    '& .MuiFormControlLabel-label': {
      marginLeft: '13px',
      lineHeight: '20px',
    },
  },
  footerCancel: {
    padding: '8px 12px',
    fontFamily: 'Nunito',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: '#0F5DB5',
  },
  footerSave: {
    padding: '8px 24px',
    borderRadius: '100px',
    fontFamily: 'Nunito',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.1px',
    backgroundColor: '#3776CF',
    '&:hover': {
      backgroundColor: '#2f68b8',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
    },
  },
})

const buildInitialValues = (group: EditGroupSettingsDialogProps['group']): GroupSettingsFormValues => ({
  automaticHourLogging: group.automaticHourLogging,
  applicationNotificationPersons: group.applicationNotificationPersons || [],
})

const areValuesDirty = (values: GroupSettingsFormValues, initialValues: GroupSettingsFormValues) => {
  if (values.automaticHourLogging !== initialValues.automaticHourLogging) {
    return true
  }

  const initialPersonIds = sortBy(initialValues.applicationNotificationPersons.map(({ id }) => id))
  const currentPersonIds = sortBy(values.applicationNotificationPersons.map(({ id }) => id))

  return !isEqual(initialPersonIds, currentPersonIds)
}

export default function EditGroupSettingsDialog({
  showing,
  close,
  complete,
  group,
  onSubmit,
}: EditGroupSettingsDialogProps) {
  const classes = useStyles()
  const isAutomaticEventHourLoggingEnabled = useFeatureEnabled('AutomaticEventHourLogging')
  const isVolunteerApplicationEnabled = useFeatureEnabled('VolunteerApplication')
  const initialValues = useMemo(() => buildInitialValues(group), [group])

  const handleSubmit = useCallback(
    async (values: GroupSettingsFormValues) => {
      const payload: {
        automaticHourLogging?: boolean
        applicationNotificationPersonIds?: number[]
      } = {}

      if (isAutomaticEventHourLoggingEnabled) {
        payload.automaticHourLogging = values.automaticHourLogging
      }

      if (isVolunteerApplicationEnabled) {
        payload.applicationNotificationPersonIds = values.applicationNotificationPersons.map(({ id }) => id)
      }

      await onSubmit(payload)
      complete(true)
    },
    [complete, isAutomaticEventHourLoggingEnabled, isVolunteerApplicationEnabled, onSubmit],
  )

  return (
    <Formik initialValues={initialValues} validationSchema={settingsSchema} onSubmit={handleSubmit} enableReinitialize>
      {({ submitForm, isSubmitting, values, setFieldValue, isValid }) => {
        const isDirty = areValuesDirty(values, initialValues)

        return (
          <FormModal showing={showing} close={close} id="edit-group-settings-dialog" title="Edit Group Settings">
            <FormModalScreen
              onBack={close}
              backText="Cancel"
              onNext={submitForm}
              nextText="Save"
              nextDisabled={!isDirty || !isValid}
              disabled={isSubmitting}
              nextIsLoading={isSubmitting}
              backClassName={classes.footerCancel}
              nextClassName={classes.footerSave}
            >
              <div className={classes.modalContent}>
                {isAutomaticEventHourLoggingEnabled && (
                  <div className={classes.choiceSection}>
                    <span className={classes.sectionTitle}>Check-In for Events:</span>
                    <div className={classes.radioIndent}>
                      <RadioGroupFormField
                        name="automaticHourLogging"
                        valueIsBoolean
                        disabled={isSubmitting}
                        options={[
                          { label: 'Group members need to manually check-in', value: false },
                          { label: 'Group members will be automatically checked-in', value: true },
                        ]}
                      />
                    </div>
                  </div>
                )}
                {isVolunteerApplicationEnabled && (
                  <div className={classes.emailSection}>
                    <span className={classes.sectionTitle}>Email Notifications:</span>
                    <p className={classes.helperText}>
                      The following contacts will receive email notifications when a volunteer has applied to this
                      group.
                    </p>
                    <VolunteerSelector
                      volunteers={values.applicationNotificationPersons}
                      onChange={persons => setFieldValue('applicationNotificationPersons', persons)}
                      placeholder="Select Recipients"
                      label={values.applicationNotificationPersons.length ? 'Recipients' : undefined}
                      maxLength={65}
                      maxWidth={600}
                    />
                  </div>
                )}
              </div>
            </FormModalScreen>
          </FormModal>
        )
      }}
    </Formik>
  )
}
